import { isBrowser } from '@websolutespa/bom-core';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';

export type ColorScheme = ('light' | 'dark') & string;

type Props = {
  colorScheme: ColorScheme;
};

type Actions = {
  setColorScheme(colorScheme: ColorScheme): void;
  toggleColorScheme(): void;
};

type State = Props & {
  actions: Actions;
};

const QUERY = '(prefers-color-scheme: dark)';

export function getPreferredColorScheme(): ColorScheme {
  return (isBrowser && window.matchMedia(QUERY).matches) ? 'dark' : 'light';
}

const updateClassList = (colorScheme: ColorScheme) => {
  // console.log('useColorScheme.updateClassList', colorScheme);
  if (isBrowser) {
    document.documentElement.classList.add('--force-reduced-motion');
    document.documentElement.classList.toggle('--scheme-dark', colorScheme === 'dark');
    document.documentElement.classList.toggle('--scheme-light', colorScheme === 'light');
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('--force-reduced-motion');
    });
  }
};

const createColorSchemeStore = ({
  name,
  storage,
  ...initialState
}: {
  name: string,
  storage?: StateStorage
} & Partial<Props>) => {
  const defaultState: Props = {
    colorScheme: 'light',
  };
  const useStore = createStore<State>()(persist(
    (set, get) => ({
      ...defaultState,
      ...initialState,
      actions: {
        setColorScheme: (colorScheme: ColorScheme) => {
          updateClassList(colorScheme);
          set(state => ({ colorScheme }));
        },
        toggleColorScheme: () => {
          const state = get();
          const newColorScheme = state.colorScheme === 'light' ? 'dark' : 'light';
          updateClassList(newColorScheme);
          set(state => ({ colorScheme: newColorScheme }));
        },
      },
    }),
    {
      name,
      storage: createJSONStorage(() => storage || sessionStorage),
      merge: (persistedState: any, currentState: State) => {
        const newColorScheme = persistedState?.colorScheme || getPreferredColorScheme();
        updateClassList(newColorScheme);
        currentState.colorScheme = newColorScheme;
        // console.log('useColorScheme.hydration', currentState);
        return currentState;
      },
      // getStorage: () => {
      //  return storage || sessionStorage;
      // },
      // onRehydrateStorage: () => () => {
      //  console.log('onRehydrateStorage', useStore, useStore.getState());
      // },
    }
  ));
  return useStore;
};

let colorSchemeSingletonStore_: ReturnType<typeof createColorSchemeStore> | undefined;
const createColorSchemeSingletonStore = () => {
  if (!colorSchemeSingletonStore_) {
    colorSchemeSingletonStore_ = createColorSchemeStore({ name: 'prefers-color-scheme' });
  }
  return colorSchemeSingletonStore_;
};

export function useColorScheme<T>(selector: (state: State) => T): T {
  return useStore(createColorSchemeSingletonStore(), selector);
}

/*
.--scheme-dark { }
@media (prefers-color-scheme: dark) {
}
.--scheme-light { }
@media (prefers-color-scheme: light) {
}
@media (prefers-color-scheme: no-preference) {
}
*/
